import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { KanbanTask } from '../../stores/KanbanTaskStores';

export type BoardFilters = {
  search: string;
  priority?: string;
  assignee?: string;
  /** `"<isoStart>_<isoEnd>"`; either half may be empty. */
  createdDate?: string;
  startDate?: string;
  dueDate?: string;
  view: 'kanban' | 'list';
};

export type BoardFiltersApi = {
  filters: BoardFilters;
  setFilter: (key: keyof BoardFilters, value: string | undefined) => void;
  clearFilters: () => void;
  /** What the toolbar badges: the query-string filters, not the search box or the view. */
  activeCount: number;
};

const URL_KEYS = ['priority', 'assignee', 'createdDate', 'startDate', 'dueDate'] as const;

/**
 * Board filters live in the query string, so a link carries them. The search box is the one
 * exception — it was never in the URL and typing into it must not push history entries.
 */
export function useBoardFilters(): BoardFiltersApi {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');

  const filters = useMemo<BoardFilters>(() => {
    const params = new URLSearchParams(location.search);
    return {
      search,
      priority: params.get('priority') ?? undefined,
      assignee: params.get('assignee') ?? undefined,
      createdDate: params.get('createdDate') ?? undefined,
      startDate: params.get('startDate') ?? undefined,
      dueDate: params.get('dueDate') ?? undefined,
      view: params.get('view') === 'list' ? 'list' : 'kanban',
    };
  }, [location.search, search]);

  const setFilter = useCallback(
    (key: keyof BoardFilters, value: string | undefined) => {
      if (key === 'search') {
        setSearch(value ?? '');
        return;
      }
      const params = new URLSearchParams(location.search);
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      navigate({ search: params.toString() }, { replace: true });
    },
    [location.search, navigate],
  );

  const clearFilters = useCallback(() => {
    setSearch('');
    const params = new URLSearchParams(location.search);
    URL_KEYS.forEach((key) => params.delete(key));
    navigate({ search: params.toString() }, { replace: true });
  }, [location.search, navigate]);

  const activeCount = useMemo(() => URL_KEYS.filter((key) => !!filters[key]).length, [filters]);

  return { filters, setFilter, clearFilters, activeCount };
}

/** `"<isoStart>_<isoEnd>"` as a `DatePicker.RangePicker` value. */
export function rangeValue(raw: string | undefined): [Dayjs | null, Dayjs | null] | null {
  if (!raw) return null;
  const [start, end] = raw.split('_');
  const from = start ? dayjs(start) : null;
  const to = end ? dayjs(end) : null;
  if (!from?.isValid() && !to?.isValid()) return null;
  return [from && from.isValid() ? from : null, to && to.isValid() ? to : null];
}

/** The inverse: what `setFilter` stores for a picker value. */
export function rangeParam(value: [Dayjs | null, Dayjs | null] | null): string | undefined {
  if (!value || (!value[0] && !value[1])) return undefined;
  return `${value[0]?.toISOString() ?? ''}_${value[1]?.toISOString() ?? ''}`;
}

function bounds(raw: string | undefined, now: Dayjs): [Dayjs | null, Dayjs | null] | null {
  if (!raw) return null;
  const [start, end] = raw.split('_');
  // A half that is not a date at all only reaches here from a hand-edited URL. Reading it as today
  // keeps the filter meaningful; comparing against an invalid dayjs silently let every task through.
  const from = start ? (dayjs(start).isValid() ? dayjs(start) : now) : null;
  const to = end ? (dayjs(end).isValid() ? dayjs(end) : now) : null;
  if (!from && !to) return null;
  return [from ? from.startOf('day') : null, to ? to.endOf('day') : null];
}

function inRange(value: string | Dayjs | null | undefined, range: [Dayjs | null, Dayjs | null] | null): boolean {
  if (!range) return true;
  if (!value) return false;
  const date = dayjs(value);
  const [from, to] = range;
  if (from && date.isBefore(from)) return false;
  if (to && date.isAfter(to)) return false;
  return true;
}

function matchesQuery(task: KanbanTask, query: string): boolean {
  if (!query) return true;
  return Boolean(
    task.title?.toLowerCase().includes(query) ||
      task.summary?.toLowerCase().includes(query) ||
      task.publicId?.toLowerCase().includes(query),
  );
}

/**
 * The board and the list read the same rows in the same order. No store and no React in sight,
 * which is what makes it testable. Soft-deleted rows are not filtered here: the page chooses the
 * source list (`deletedProjectTasks` for the deleted view, `projectTasks` otherwise) and the
 * deleted view is filtered by search/priority/assignee just like the live one.
 */
export function applyBoardFilters(tasks: KanbanTask[], filters: BoardFilters, now: Dayjs = dayjs()): KanbanTask[] {
  const query = filters.search.trim().toLowerCase();
  const priority = filters.priority !== undefined && filters.priority !== '' ? Number(filters.priority) : null;
  const created = bounds(filters.createdDate, now);
  const start = bounds(filters.startDate, now);
  const due = bounds(filters.dueDate, now);

  return tasks
    .filter((task) => matchesQuery(task, query))
    .filter((task) => priority === null || Number.isNaN(priority) || task.priority === priority)
    .filter((task) => !filters.assignee || String(task.assigneeId) === String(filters.assignee))
    .filter((task) => inRange(task.createdDate, created))
    .filter((task) => inRange(task.startDate, start))
    .filter((task) => inRange(task.dueDate, due))
    .sort((a, b) => a.index - b.index);
}
